class Hotspots extends HTMLElement {
  // Hover intent: short enough to feel instant, long enough to ignore a pointer passing over a hotspot.
  static OPEN_DELAY = 100;
  // Grace period so the pointer can travel from the hotspot to its card without it closing.
  static CLOSE_DELAY = 200;
  static CARD_OFFSET = 12;
  static VIEWPORT_GUTTER = 12;

  constructor() {
    super();

    this.active = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onDocumentPointerDown = this.#onDocumentPointerDown.bind(this);
    this.onDocumentKeyDown = this.#onDocumentKeyDown.bind(this);
    this.onViewportChange = this.#onViewportChange.bind(this);
  }

  connectedCallback() {
    this.querySelectorAll('.hotspot').forEach((hotspot) => {
      if (!hotspot.querySelector('.hotspot-card')) return;

      hotspot.addEventListener('pointerenter', (event) => this.#onPointerEnter(event, hotspot));
      hotspot.addEventListener('pointerleave', (event) => this.#onPointerLeave(event, hotspot));
      hotspot.addEventListener('focusout', (event) => this.#onFocusOut(event, hotspot));
      hotspot.querySelector('.hotspot__button').addEventListener('click', () => this.#onClick(hotspot));
      hotspot
        .querySelector('.hotspot-card__close')
        .addEventListener('click', () => this.#close({ restoreFocus: true }));
    });

    this.addEventListener('shopify:block:select', (event) => {
      const hotspot = event.target.closest('.hotspot');
      if (hotspot?.querySelector('.hotspot-card')) this.#open(hotspot, { pinned: true });
    });
    this.addEventListener('shopify:block:deselect', () => this.#close());

    this.#preloadImagesWhenNear();
    this.#fireEvent('hotspot_image', 'view_section', 'Hotspot image');
  }

  disconnectedCallback() {
    this.#clearTimers();
    this.#toggleDocumentListeners(false);
    this.imageObserver?.disconnect();
  }

  #onPointerEnter(event, hotspot) {
    if (event.pointerType !== 'mouse') return;

    clearTimeout(this.closeTimer);
    if (this.active?.hotspot === hotspot) return;

    // Moving between hotspots while a card is already showing should switch immediately.
    const delay = this.active ? 0 : Hotspots.OPEN_DELAY;
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      this.#open(hotspot, { pinned: false });
      this.#fireEvent('rtd_hotspot_image', 'hover_on_hotspot', this.#productTitle(hotspot));
    }, delay);
  }

  #onPointerLeave(event, hotspot) {
    if (event.pointerType !== 'mouse') return;

    clearTimeout(this.openTimer);
    if (this.active?.hotspot !== hotspot || this.active.pinned) return;

    this.closeTimer = setTimeout(() => this.#close(), Hotspots.CLOSE_DELAY);
  }

  #onClick(hotspot) {
    if (this.active?.hotspot === hotspot && this.active.pinned) {
      this.#close();
      return;
    }

    // A click on a hover-opened card pins it rather than toggling it shut.
    this.#open(hotspot, { pinned: true });
    this.#fireEvent('rtd_hotspot_image', 'view_hotspot', this.#productTitle(hotspot));
  }

  #onFocusOut(event, hotspot) {
    if (this.active?.hotspot !== hotspot) return;
    // relatedTarget is null when clicking non-focusable content; outside clicks are handled on pointerdown.
    if (event.relatedTarget && !hotspot.contains(event.relatedTarget)) this.#close();
  }

  #onDocumentPointerDown(event) {
    if (!this.active.hotspot.contains(event.target)) this.#close();
  }

  #onDocumentKeyDown(event) {
    if (event.key === 'Escape') this.#close({ restoreFocus: true });
  }

  #onViewportChange() {
    if (this.repositionFrame) return;

    this.repositionFrame = requestAnimationFrame(() => {
      this.repositionFrame = null;
      if (!this.active) return;

      const anchor = this.active.button.getBoundingClientRect();
      if (anchor.bottom < 0 || anchor.top > window.innerHeight) {
        this.#close();
        return;
      }
      this.#position();
    });
  }

  #open(hotspot, { pinned }) {
    this.#clearTimers();

    if (this.active?.hotspot === hotspot) {
      this.active.pinned ||= pinned;
      return;
    }

    this.#close();

    const button = hotspot.querySelector('.hotspot__button');
    const card = hotspot.querySelector('.hotspot-card');

    card.getAnimations().forEach((animation) => animation.cancel());
    if (!card.matches(':popover-open')) card.showPopover();

    this.active = { hotspot, button, card, pinned };
    button.setAttribute('aria-expanded', 'true');

    this.#position();
    this.#animate(card, true);
    this.#toggleDocumentListeners(true);
  }

  #close({ restoreFocus = false } = {}) {
    this.#clearTimers();
    if (!this.active) return;

    const { hotspot, button, card } = this.active;
    this.active = null;

    button.setAttribute('aria-expanded', 'false');
    this.#toggleDocumentListeners(false);
    if (restoreFocus && hotspot.contains(document.activeElement)) button.focus();

    // A cancelled exit (card reopened mid-animation) rejects `finished`, leaving the card open.
    this.#animate(card, false)
      .finished.then(() => card.hidePopover())
      .catch(() => {});
  }

  #position() {
    const { button, card } = this.active;
    const anchor = button.getBoundingClientRect();
    const width = card.offsetWidth;
    const height = card.offsetHeight;
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight;
    const offset = Hotspots.CARD_OFFSET;
    const gutter = Hotspots.VIEWPORT_GUTTER;

    // Prefer sitting beside the hotspot, falling back to below/above on narrow screens.
    let placement = 'top';
    if (anchor.right + offset + width <= viewportWidth - gutter) placement = 'right';
    else if (anchor.left - offset - width >= gutter) placement = 'left';
    else if (anchor.bottom + offset + height <= viewportHeight - gutter) placement = 'bottom';

    let left;
    let top;
    if (placement === 'right' || placement === 'left') {
      left = placement === 'right' ? anchor.right + offset : anchor.left - offset - width;
      top = anchor.top + anchor.height / 2 - height / 2;
    } else {
      left = anchor.left + anchor.width / 2 - width / 2;
      top = placement === 'bottom' ? anchor.bottom + offset : anchor.top - offset - height;
    }

    const clamp = (value, max) => Math.min(Math.max(value, gutter), Math.max(gutter, max - gutter));
    card.style.left = `${clamp(left, viewportWidth - width)}px`;
    card.style.top = `${clamp(top, viewportHeight - height)}px`;
    card.dataset.placement = placement;
  }

  #animate(card, opening) {
    card.getAnimations().forEach((animation) => animation.cancel());

    // Nudge the card in from the hotspot's side so it reads as coming from it.
    const [x, y] = { right: [-8, 0], left: [8, 0], bottom: [0, -8], top: [0, 8] }[card.dataset.placement];
    const shown = { opacity: 1, transform: 'none' };
    const hidden = this.reducedMotion.matches
      ? { opacity: 0, transform: 'none' }
      : { opacity: 0, transform: `translate(${x}px, ${y}px) scale(0.96)` };

    return card.animate(opening ? [hidden, shown] : [shown, hidden], {
      duration: opening ? 220 : 140,
      easing: opening ? 'cubic-bezier(0.16, 1, 0.3, 1)' : 'cubic-bezier(0.4, 0, 1, 1)',
      fill: opening ? 'none' : 'forwards',
    });
  }

  #toggleDocumentListeners(add) {
    const method = add ? 'addEventListener' : 'removeEventListener';
    document[method]('pointerdown', this.onDocumentPointerDown);
    document[method]('keydown', this.onDocumentKeyDown);
    window[method]('scroll', this.onViewportChange, { passive: true, capture: true });
    window[method]('resize', this.onViewportChange, { passive: true });
  }

  #clearTimers() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
  }

  // Card images are lazy and hidden, so they wouldn't load until first open; fetch them as the section approaches.
  #preloadImagesWhenNear() {
    this.imageObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        this.querySelectorAll('.hotspot-card__image').forEach((image) => (image.loading = 'eager'));
        this.imageObserver.disconnect();
      },
      { rootMargin: '300px' },
    );
    this.imageObserver.observe(this.closest('.banner__media') || this);
  }

  #productTitle(hotspot) {
    return hotspot.querySelector('.hotspot__button').dataset.productTitle;
  }

  #fireEvent(eventContext, eventAction, eventValue) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'site_interaction',
      context: eventContext,
      action: eventAction,
      value: eventValue,
    });
  }
}

if (!customElements.get('hotspots-image')) {
  window.customElements.define('hotspots-image', Hotspots);
}
